import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useCountdown } from '@renderer/utils/countdownUtils'
import { unifiedCameraUtils } from '@renderer/utils/cameras/uniCamUtils'
import Preview from '@renderer/components/Preview'
import { flashEffect } from '@renderer/utils/flashUtils'
import { useConfigStore } from '@renderer/stores/configState'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { useCreditsStore } from '@renderer/stores/creditsStore'
import cogoToast from '@successtar/cogo-toast'
import { useTranslation } from 'react-i18next'
import { useSharedCountdown } from './hooks/useSharedCountdown'
import { filterUtils } from '@renderer/utils/filterUtils'
import { applyLUTThenOverlay, applyFilterOverlayImage} from '@renderer/utils/lutUtils'
import { Recorder, RecorderStatus } from 'canvas-record'
import { AVC } from 'media-codecs'
import { getNormalizedCaptureRatio as getSharedNormalizedCaptureRatio } from '@renderer/utils/captureRatioUtils'
import FrameCanvasBackdrop from '@renderer/components/FrameCanvasBackdrop'
import { evenDimensions, unrecordableReason } from '@renderer/utils/recorderDims'

const CapturePage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { setSessionState, userPreferences, id, uploadSessionState, logActivity } =
    useSessionStateStore()
  const { config: globalConfig, filters, getFrame } = useConfigStore()
  const config = globalConfig?.pages.capture
  const { downloadLocal, cameraSettings } = useSettingsStore()
  const { fetchSubscriptionAndCredits } = useCreditsStore()

  // Get selected filter from user preferences, fallback to first filter
  const selectedFilterId = userPreferences.filterId
  const selectedFilter = selectedFilterId
    ? filters.find((f) => f._id === selectedFilterId) || filters[0]
    : filters[0]
  const filterAdjustments = selectedFilter?.adjustments

  // Get selected frame
  const selectedFrame = getFrame(userPreferences.frameId || '')
  if (!selectedFrame) {
    // Navigate back to frame selection if no frame selected
    navigate(`/${projectId}/frame`)
    return null
  }

  // Get unique photo types from frame (like default Capture.tsx)
  const uniquePhotoTypes = new Set(
    selectedFrame.drawParams.images
      .map((image) => image.type)
      .filter((type) => type.startsWith('photo-'))
  )
  const uniquePhotoCount = uniquePhotoTypes.size
  const captureCount = Math.max(uniquePhotoCount, config?.settings.captureCount || 1)

  // Get all photo slots for display
  const photoSlots = selectedFrame.drawParams.images.filter((img) => img.type.startsWith('photo-'))

  // Convert Set to sorted array for indexing
  const uniquePhotoTypesArray = Array.from(uniquePhotoTypes).sort()

  // Shared countdown
  const { remainingTime, isExpired, startCountdown } = useSharedCountdown()

  // Capture state - track photos by type (photo-1, photo-2, etc.)
  const [currentPhotoTypeIndex, setCurrentPhotoTypeIndex] = useState<number>(0)
  const [capturedPhotosByType, setCapturedPhotosByType] = useState<Map<string, string>>(new Map())
  // Check if coming from retake page - skip prep countdown if true
  const isRetakeFromRetakePage = userPreferences.isRetakeFromRetakePage || false
  const [preparing, setPreparing] = useState(!isRetakeFromRetakePage)
  const [capturing, setCapturing] = useState(false)
  const [shouldNavigateOnTimeout, setShouldNavigateOnTimeout] = useState(false)
  const [prepCountdown, setPrepCountdown] = useState<string>('00:00')
  const [captureCountdown, setCaptureCountdown] = useState<number>(
    config?.settings.captureCountdown || 3
  )
  const [canvasDimensions, setCanvasDimensions] = useState<{
    width: number
    height: number
  } | null>(null)
  const [isCaptureError, setIsCaptureError] = useState(false)

  const prepCountdownHook = useCountdown(config?.settings.prepTimeout || 5)
  const captureCountdownHook = useCountdown(config?.settings.captureCountdown || 3, { audioCue: true })
  const stopCaptureRef = useRef<boolean>(false)
  const captureLockRef = useRef<boolean>(false)

  // Video recording setup
  const useRecording = userPreferences.useRecording
  // const recordingSpeed = globalConfig?.pages.capture?.settings?.recordingSpeed || 1 // Unused for now
  const targetDuration = (config?.settings.captureCountdown || 3) * 1000
  const FPS = 30
  const totalFrames = Math.ceil((targetDuration * FPS) / 1000)
  const captureCanvasRecorderRef = useRef<Recorder | null>(null)
  const captureCanvasElementRef = useRef<HTMLCanvasElement | null>(null)
  const recordingActiveRef = useRef<boolean>(false)
  const recordingPromiseRef = useRef<Promise<void> | null>(null)

  // Calculate scale for fullscreen frame display using actual screenSize
  const screenWidth = globalConfig?.screenSize.width || 1080
  const screenHeight = globalConfig?.screenSize.height || 1920
  const frameWidth = selectedFrame.drawParams.canvasWidth
  const frameHeight = selectedFrame.drawParams.canvasHeight

  // Scale frame to fit screen while maintaining aspect ratio
  // Account for padding, top gap, and bottom whitespace for buttons (countdown + progress indicator)
  const paddingSize = 16
  const topGap = 80
  const buttonAreaHeight = 180
  const availableWidth = screenWidth - paddingSize * 2
  const availableHeight = screenHeight - paddingSize * 2 - topGap - buttonAreaHeight
  const scale = Math.min(availableWidth / frameWidth, availableHeight / frameHeight)

  const getNormalizedCaptureRatio = (): number | undefined =>
    getSharedNormalizedCaptureRatio(cameraSettings.captureRatio)

  // Get photo ratio from frame
  const getPhotoRatio = (slot: (typeof photoSlots)[0]): number => {
    return slot.width / slot.height
  }

  // H.264 requires even dimensions
  const validateDimensions = evenDimensions

  // Initialize recorder when canvas is ready
  useEffect(() => {
    if (canvasDimensions) {
      const canvas = document.querySelector('canvas')
      if (!canvas) return

      if (
        useRecording &&
        (userPreferences.recordingType === 'strip_live' ||
          userPreferences.recordingType === 'strip_story')
      ) {
        // CRITICAL: Don't create a new recorder if one already exists and is actively recording
        // This prevents the useEffect from overwriting manually created recorders during capture
        if (captureCanvasRecorderRef.current) {
          const currentStatus = captureCanvasRecorderRef.current.status
          // If recorder exists and is Recording, don't overwrite it (it's actively recording)
          if (currentStatus === RecorderStatus.Recording) {
            console.log(`[Capture] Recorder already exists and is Recording (status: ${currentStatus}), skipping creation in useEffect`)
            return
          }
          // If recorder is Ready (created but not started), keep it - it might be a manually created recorder
          // that's about to be started
          if (currentStatus === RecorderStatus.Ready) {
            console.log(`[Capture] Recorder already exists and is Ready (status: ${currentStatus}), keeping existing recorder`)
            return
          }
          // If recorder is Stopped, clean it up first before creating a new one
          console.log(`[Capture] Existing recorder is stopped (status: ${currentStatus}), cleaning up before creating new one`)
          try {
            captureCanvasRecorderRef.current.stop()
          } catch {
            // Ignore errors
          }
          captureCanvasRecorderRef.current = null
          captureCanvasElementRef.current = null
        }

        // H.264 requires even dimensions - validate and specify in encoderOptions
        // Don't resize the canvas itself (it's used by Preview component)
        const validatedDims = validateDimensions(canvasDimensions.width, canvasDimensions.height)
        // A slot too thin to film gets no recorder; handleCapture logs the missing clip.
        const tooSmall = unrecordableReason(validatedDims.width, validatedDims.height)
        if (tooSmall) {
          console.warn(`[Capture] Not creating a recorder: ${tooSmall}`)
          return
        }

        // Get context (don't modify canvas)
        const canvasContext = canvas.getContext('bitmaprenderer')
        if (!canvasContext) {
          console.error('[Capture] Failed to get bitmaprenderer context')
          return
        }

        console.log(
          `[Capture] Creating recorder with validated dimensions: ${validatedDims.width}x${validatedDims.height} (original: ${canvasDimensions.width}x${canvasDimensions.height})`
        )

        try {
          const newCaptureRecorder = new Recorder(canvasContext as RenderingContext, {
            name: `dreambooth-${id}-capture`,
            encoderOptions: {
              codec: AVC.getCodec({ profile: 'Baseline', level: '4.1' }),
              width: validatedDims.width,
              height: validatedDims.height,
              bitrate: 2000000
            },
            duration: Infinity,
            download:
              downloadLocal &&
              (userPreferences.recordingType === 'strip_live' ||
                userPreferences.recordingType === 'strip_story'),
            frameRate: FPS
          })
          captureCanvasRecorderRef.current = newCaptureRecorder
          captureCanvasElementRef.current = canvas
          console.log(`[Capture] Recorder created successfully with encoder dimensions: ${validatedDims.width}x${validatedDims.height}`)
        } catch (error) {
          const errorMsg = error instanceof Error ? error.message : String(error)
          console.error('[Capture] Error creating recorder:', errorMsg, error)
          // Continue without recording if recorder creation fails
          captureCanvasRecorderRef.current = null
        }
      }
    }

    return (): void => {
      // Only stop recorder on unmount, not when dependencies change
      // This prevents stopping a recorder that's actively being used
      // Note: We don't include 'capturing' in dependencies to avoid re-running this effect
      // when capturing state changes, which would stop an active recorder
    }
  }, [canvasDimensions, useRecording, userPreferences.recordingType, downloadLocal, id, FPS])

  // Handle timeout expiration - wait for current capture to finish, then navigate to Select
  useEffect(() => {
    if (isExpired && !shouldNavigateOnTimeout) {
      logActivity('Shared countdown expired on Capture page')
      // If currently capturing, wait for it to finish
      if (capturing) {
        setShouldNavigateOnTimeout(true)
        console.log('[Capture] Timeout expired during capture, will navigate after capture completes')
      } else {
        // Not capturing, navigate immediately to Select (which will auto-process)
        navigate(`/${projectId}/select`)
      }
    }
  }, [isExpired, capturing, shouldNavigateOnTimeout])

  // Navigate after capture completes if timeout expired
  useEffect((): (() => void) | void => {
    if (shouldNavigateOnTimeout && !capturing && !preparing) {
      // Wait a moment to ensure capture is fully saved
      const timer = setTimeout(() => {
        console.log('[Capture] Navigating to Select after timeout and capture completion')
        navigate(`/${projectId}/select`)
      }, 500)
      return (): void => clearTimeout(timer)
    }
  }, [shouldNavigateOnTimeout, capturing, preparing])

  // Initialize capture session
  useEffect(() => {
    uploadSessionState()

    // Start shared countdown if not already started
    const currentPrefs = useSessionStateStore.getState().userPreferences
    if (!currentPrefs.sharedCountdownStartTime) {
      startCountdown()
    }

    // Clear retake flag if it was set
    if (isRetakeFromRetakePage) {
      setSessionState({
        userPreferences: {
          isRetakeFromRetakePage: false
        }
      })
    }

    // Set up prep countdown callbacks
    prepCountdownHook.setOnTick(() => {
      setPrepCountdown(prepCountdownHook.getFormattedRemainingSeconds())
    })

    prepCountdownHook.setOnComplete(() => {
      startCaptureSession()
    })

    // Set up capture countdown callbacks
    captureCountdownHook.setOnTick(() => {
      setCaptureCountdown(captureCountdownHook.getRemainingSeconds())
    })

    captureCountdownHook.setOnComplete(() => {
      handleCapture()
    })

    // If coming from retake page, skip prep and start capture immediately
    if (isRetakeFromRetakePage) {
      startCaptureSession()
    } else {
      // Handle auto-start when prepTimeout is null/0 (not a retake)
      const prepTimeout = config?.settings.prepTimeout
      const isRetake = userPreferences.retakeCount > 0
      if ((prepTimeout === null || prepTimeout === 0 || prepTimeout === undefined) && !isRetake) {
        // Auto-start immediately - this will trigger credit deduction in startCaptureSession()
        startCaptureSession()
      } else {
        // Start prep countdown on mount
        prepCountdownHook.start()
      }
    }

    return (): void => {
      prepCountdownHook.stop()
      captureCountdownHook.stop()
      stopCaptureRef.current = true
      captureCanvasRecorderRef.current?.stop()
    }
  }, [])

  // Update countdown callbacks when currentPhotoTypeIndex changes
  useEffect(() => {
    captureCountdownHook.setOnTick(() => {
      setCaptureCountdown(captureCountdownHook.getRemainingSeconds())
    })

    captureCountdownHook.setOnComplete(() => {
      handleCapture()
    })
  }, [currentPhotoTypeIndex])

  /** One activity-log line per shot that goes ahead without a clip, saying why. */
  const logShotWithoutClip = (
    shot: number,
    reason: string | null,
    level: 'warn' | 'error'
  ): void => {
    logActivity(
      `Capture recorder unavailable for shot ${shot} — this shot will have no video clip`,
      {
        level,
        component: 'capture_recorder',
        photo: shot,
        photoType: uniquePhotoTypesArray[shot - 1],
        reason
      }
    )
  }

  // Imperative frame recording: run inline when countdown starts (no useEffect race).
  // Called from startCaptureSession and from handleCapture next-photo branch.
  const runFrameRecordingLoop = (photoIndexForLog: number): void => {
    if (!useRecording || !captureCanvasRecorderRef.current) {
      recordingActiveRef.current = false
      return
    }
    if (captureCanvasRecorderRef.current.status !== RecorderStatus.Recording) {
      console.warn('[Capture] Recorder not in Recording status when starting frame loop', {
        status: captureCanvasRecorderRef.current.status,
        photoIndex: photoIndexForLog
      })
      return
    }
    if (recordingActiveRef.current) {
      console.log('[Capture] Frame recording already active, skipping duplicate')
      return
    }
    console.log(`[Capture] Starting imperative frame recording for photo ${photoIndexForLog}, target frames: ${totalFrames}`)
    recordingActiveRef.current = true
    let frameCount = 0
    const startTime = performance.now()

    const recordFrames = async (): Promise<void> => {
      while (
        recordingActiveRef.current &&
        frameCount < totalFrames &&
        captureCanvasRecorderRef.current?.status === RecorderStatus.Recording
      ) {
        const currentTime = performance.now()
        const elapsedTime = currentTime - startTime
        const targetFrameTime = (frameCount * 1000) / FPS

        if (elapsedTime >= targetFrameTime) {
          try {
            if (
              recordingActiveRef.current &&
              captureCanvasRecorderRef.current?.status === RecorderStatus.Recording
            ) {
              await captureCanvasRecorderRef.current.step()
              frameCount++
              if (frameCount % 10 === 0) {
                console.log(`[Capture] Recorded ${frameCount}/${totalFrames} frames for photo ${photoIndexForLog}`)
              }
            }
          } catch (error) {
            console.error(`[Capture] Error stepping capture recorder for photo ${photoIndexForLog}:`, error)
          }
        }

        await new Promise((resolve) => setTimeout(resolve, 1))
      }
      console.log(`[Capture] Finished recording frames for photo ${photoIndexForLog}: ${frameCount}/${totalFrames} frames recorded`)
    }

    recordingPromiseRef.current = recordFrames()
  }

  const handleCapture = async (): Promise<void> => {
    if (captureLockRef.current) {
      console.log('[CAPTURE] Capture already in progress, skipping duplicate')
      return
    }

    captureLockRef.current = true

    try {
      const currentPhotoType = uniquePhotoTypesArray[currentPhotoTypeIndex]
      console.log(
        `Starting capture process for photo type ${currentPhotoType} (${currentPhotoTypeIndex + 1} of ${captureCount})`
      )

      flashEffect('.flash')
      
      // CRITICAL: Stop frame recording BEFORE stopping the recorder
      // This ensures all frames are captured before we stop
      recordingActiveRef.current = false
      
      // Wait a moment for frame recording to finish current step
      await new Promise((resolve) => setTimeout(resolve, 100))
      
      setCapturing(false)

      await new Promise((resolve) => setTimeout(resolve, 200))

      // Stop and save capture recording if enabled
      let captureUrl: string | undefined = undefined
      if (useRecording && captureCanvasRecorderRef.current) {
        try {
          const recorderStatus = captureCanvasRecorderRef.current.status
          console.log(`[Capture] Stopping recorder for photo ${currentPhotoTypeIndex + 1}, current status: ${recorderStatus}`)
          
          // Check if recorder was actually recording before trying to stop
          if (recorderStatus === RecorderStatus.Recording) {
            const captureChunks = await captureCanvasRecorderRef.current.stop()
            // Handle different return types: ArrayBuffer | Blob[] | Uint8Array
            const chunksLength = Array.isArray(captureChunks) 
              ? captureChunks.length 
              : captureChunks instanceof ArrayBuffer 
                ? captureChunks.byteLength 
                : captureChunks instanceof Uint8Array 
                  ? captureChunks.length 
                  : 0
            console.log(`[Capture] Recorder stopped for photo ${currentPhotoTypeIndex + 1}, chunks length: ${chunksLength}`)
            
            if (captureChunks && chunksLength > 0) {
              const captureBlob = new Blob([captureChunks as ArrayBuffer], { type: 'video/mp4' })
              console.log(`[Capture] Created blob for photo ${currentPhotoTypeIndex + 1}, size: ${captureBlob.size} bytes`)
              
              // Validate blob size - empty blobs indicate recording failed
              if (captureBlob.size > 1000) { // Minimum 1KB to ensure it's a valid video
                captureUrl = URL.createObjectURL(captureBlob)
                console.log(`[Capture] Created video blob URL for photo ${currentPhotoTypeIndex + 1}: ${captureUrl}, size: ${captureBlob.size} bytes`)
              } else {
                console.warn(`[Capture] Video blob is too small for photo ${currentPhotoTypeIndex + 1} (${captureBlob.size} bytes), recording may have failed`)
              }
            } else {
              console.warn(`[Capture] No video chunks recorded for photo ${currentPhotoTypeIndex + 1}, recording may have failed`)
            }
          } else {
            console.warn(`[Capture] Recorder was not recording for photo ${currentPhotoTypeIndex + 1} (status: ${recorderStatus}), skipping video creation. Recorder may have failed to start.`)
          }
        } catch (error) {
          const errorMsg = error instanceof Error ? error.message : String(error)
          console.error(`[Capture] Error stopping capture recorder for photo ${currentPhotoTypeIndex + 1}:`, errorMsg, error)
        }
      } else if (useRecording) {
        console.warn(`[Capture] Recording enabled but recorder not available when stopping photo ${currentPhotoTypeIndex + 1}`)
      }

      const imageData = await unifiedCameraUtils.captureImage(
        userPreferences.selectedCamera as 'primary' | 'secondary',
        applyFilter,
        getNormalizedCaptureRatio(),
        !cameraSettings.useShutterCapture
      )

      if (imageData) {
        console.log('Successfully captured image')
        const imageUrl = URL.createObjectURL(imageData)

        if (downloadLocal) {
          const a = document.createElement('a')
          a.href = imageUrl
          a.download = `dreambooth-${id}-${Date.now()}.jpg`
          a.setAttribute('data-automated-click', 'true')
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
        }

        // Store photo by type (one photo can fill multiple slots with same type)
        const newCapturedPhotosByType = new Map(capturedPhotosByType)
        newCapturedPhotosByType.set(currentPhotoType, imageUrl)
        setCapturedPhotosByType(newCapturedPhotosByType)

        // Add to gallery
        const prevItems = useSessionStateStore.getState().gallery.capturedItems || []
        const captureId = `dreambooth-${id}-${Date.now()}-${currentPhotoTypeIndex}`
        setSessionState({
          gallery: {
            capturedItems: [
              ...prevItems,
              {
                id: captureId,
                photoUrl: imageUrl,
                recordingUrl: captureUrl || '',
                timestamp: new Date()
              }
            ]
          }
        })

        logActivity(
          `Captured photo type ${currentPhotoType} (${currentPhotoTypeIndex + 1} of ${captureCount})`,
          {
            photoType: currentPhotoType,
            photoTypeIndex: currentPhotoTypeIndex,
            totalPhotos: captureCount
          }
        )

        // Move to next photo type or finish
        if (currentPhotoTypeIndex < captureCount - 1) {
          // Stop capture countdown first
          captureCountdownHook.stop()
          // CRITICAL: Stop frame recording BEFORE stopping recorder
          recordingActiveRef.current = false
          // Wait a moment for frame recording to finish
          await new Promise((resolve) => setTimeout(resolve, 100))
          setCapturing(false)

          const nextIndex = currentPhotoTypeIndex + 1
          setCurrentPhotoTypeIndex(nextIndex)
          
          // CRITICAL: Wait for React to re-render and Preview component to mount before looking for canvas
          // This ensures the canvas element exists when we try to create the recorder
          await new Promise((resolve) => setTimeout(resolve, 200))

          // Create new recorder for next capture if enabled
          // The recorder cannot be restarted after stop(), so we need to create a new one
          // CRITICAL: Must create and start recorder BEFORE starting countdown
          let recorderReady = !useRecording // If recording disabled, we're ready
          // Why there is no recorder for this shot, for the activity log.
          let recorderFailure: string | null = null
          let slotTooThin = false

          if (useRecording) {
            try {
              let canvas: HTMLCanvasElement | null = null
              let retries = 0
              const maxRetries = 10

              while (!canvas && retries < maxRetries) {
                canvas = document.querySelector<HTMLCanvasElement>('canvas[data-preview-type="camera"]') ?? document.querySelector('canvas')
                if (!canvas) {
                  retries++
                  if (retries < maxRetries) {
                    console.log(`[Capture] Canvas not found for photo ${nextIndex + 1}, retrying... (${retries}/${maxRetries})`)
                    await new Promise((resolve) => setTimeout(resolve, 100))
                  }
                } else {
                  console.log(`[Capture] Canvas found for photo ${nextIndex + 1} after ${retries} retries`)
                }
              }

              if (!canvas) {
                console.error(`[Capture] Canvas not found for photo ${nextIndex + 1} after ${maxRetries} retries`)
                recorderFailure = 'preview canvas not found'
                recorderReady = false
              } else {
                // Use canvas element dimensions (source of truth for current slot); avoid stale canvasDimensions state
                let canvasWidth = canvas.width
                let canvasHeight = canvas.height
                if (!canvasWidth || !canvasHeight) {
                  for (let wait = 0; wait < 5; wait++) {
                    await new Promise((r) => setTimeout(r, 50))
                    canvasWidth = canvas.width
                    canvasHeight = canvas.height
                    if (canvasWidth && canvasHeight) break
                  }
                }
                const tooSmall =
                  canvasWidth && canvasHeight ? unrecordableReason(canvasWidth, canvasHeight) : null
                if (!canvasWidth || !canvasHeight) {
                  console.error(`[Capture] Cannot determine canvas dimensions for photo ${nextIndex + 1}`, {
                    canvasDimensions,
                    canvasWidth: canvas.width,
                    canvasHeight: canvas.height
                  })
                  recorderFailure = 'preview canvas has no size'
                  recorderReady = false
                } else if (tooSmall) {
                  // The encoder would refuse it on every attempt, so don't build one.
                  console.warn(`[Capture] Not recording photo ${nextIndex + 1}: ${tooSmall}`)
                  recorderFailure = tooSmall
                  slotTooThin = true
                  recorderReady = false
                } else {
                  const existingRecorder = captureCanvasRecorderRef.current
                  const sameCanvas = captureCanvasElementRef.current === canvas

                  // Reuse recorder if same canvas and recorder exists and is stopped
                  if (sameCanvas && existingRecorder && existingRecorder.status === RecorderStatus.Stopped) {
                    console.log(`[Capture] Reusing recorder for photo ${nextIndex + 1} (same canvas)`)
                    try {
                      await existingRecorder.start()
                      if (captureCanvasRecorderRef.current?.status === RecorderStatus.Recording) {
                        recorderReady = true
                      } else {
                        recorderFailure = `reused recorder is in status ${captureCanvasRecorderRef.current?.status} after start()`
                        captureCanvasRecorderRef.current = null
                        recorderReady = false
                      }
                    } catch (e) {
                      console.warn('[Capture] Error reusing recorder:', e)
                      recorderFailure = `reused recorder failed to start: ${e instanceof Error ? e.message : String(e)}`
                      captureCanvasRecorderRef.current = null
                      recorderReady = false
                    }
                  } else {
                    const canvasContext = canvas.getContext('bitmaprenderer')
                    if (!canvasContext) {
                      console.error(`[Capture] Failed to get canvas context for photo ${nextIndex + 1}`)
                      recorderFailure = 'no bitmaprenderer context on the preview canvas'
                      recorderReady = false
                    } else {
                      const validatedDims = validateDimensions(canvasWidth, canvasHeight)

                      if (existingRecorder) {
                        try {
                          if (existingRecorder.status === RecorderStatus.Recording) {
                            await existingRecorder.stop()
                          }
                        } catch (e) {
                          console.warn('[Capture] Error stopping old recorder:', e)
                        }
                        captureCanvasRecorderRef.current = null
                        captureCanvasElementRef.current = null
                      }

                      await new Promise((resolve) => setTimeout(resolve, 50))

                      console.log(`[Capture] Creating new recorder for photo ${nextIndex + 1}`, {
                        dimensions: `${validatedDims.width}x${validatedDims.height}`,
                        fps: FPS
                      })

                      const newRecorder = new Recorder(canvasContext as RenderingContext, {
                        name: `dreambooth-${id}-capture-${nextIndex}`,
                        encoderOptions: {
                          codec: AVC.getCodec({ profile: 'Baseline', level: '4.1' }),
                          width: validatedDims.width,
                          height: validatedDims.height,
                          bitrate: 2000000
                        },
                        duration: Infinity,
                        download:
                          downloadLocal &&
                          (userPreferences.recordingType === 'strip_live' ||
                            userPreferences.recordingType === 'strip_story'),
                        frameRate: FPS
                      })
                      captureCanvasRecorderRef.current = newRecorder
                      captureCanvasElementRef.current = canvas

                      console.log(`[Capture] Starting recorder for photo ${nextIndex + 1}...`)
                      await captureCanvasRecorderRef.current.start()

                      const recorderStatus = captureCanvasRecorderRef.current.status
                      if (recorderStatus !== RecorderStatus.Recording) {
                        console.error(`[Capture] Recorder failed to start for photo ${nextIndex + 1}, status: ${recorderStatus}`)
                        recorderFailure = `recorder is in status ${recorderStatus} after start()`
                        captureCanvasRecorderRef.current = null
                        recorderReady = false
                      } else {
                        recorderReady = true
                      }
                    }
                  }
                }
              }
            } catch (error) {
              const errorMsg = error instanceof Error ? error.message : String(error)
              console.error(`[Capture] Error creating/starting recorder for photo ${nextIndex + 1}:`, errorMsg)
              recorderFailure = `recorder failed to start: ${errorMsg}`
              captureCanvasRecorderRef.current = null
              recorderReady = false
            }
          }

          if (!recorderReady) {
            // The clip is extra; the photo is what the guest is here for. Take the shot without
            // it, as FrameBasedCapture does, and the framed video shows this slot as a still.
            // This used to stop on "Capture Failed - please stay still": a recorder problem the
            // guest cannot fix, which Retry ran into again (82 sessions in a week, all at shot 3
            // on the two frames whose photo-3 slot is 2 px tall), while the session clock ran.
            captureCanvasRecorderRef.current = null
            logShotWithoutClip(nextIndex + 1, recorderFailure, slotTooThin ? 'warn' : 'error')
          }

          setPreparing(false)
          const captureTime = config?.settings.captureCountdown || 3
          captureCountdownHook.setRemainingSeconds(captureTime)
          setCaptureCountdown(captureTime)
          // Autofocus now, while the countdown runs, so the shutter is not waiting on it (camera_prefocus).
          unifiedCameraUtils.prefocus()
          captureCountdownHook.start()
          setCapturing(true)
          runFrameRecordingLoop(nextIndex + 1)
        } else {
          // All photos captured
          logActivity('All photos captured', {
            totalPhotos: captureCount
          })
          uploadSessionState()
          
          // If timeout expired, go directly to Select (which will auto-process)
          // Otherwise go to Retake
          if (shouldNavigateOnTimeout || isExpired) {
            navigate(`/${projectId}/select`)
          } else {
            navigate(`/${projectId}/retake`)
          }
        }
      } else {
        logActivity('Capture processing failed: No image data captured.', {
          level: 'error',
          photoTypeIndex: currentPhotoTypeIndex
        })
        setIsCaptureError(true)
      }
    } catch (error) {
      console.error('Capture failed:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      logActivity(`Capture processing failed: ${errorMessage}`, {
        error: errorMessage,
        level: 'error',
        photoTypeIndex: currentPhotoTypeIndex
      })
      setIsCaptureError(true)
    } finally {
      captureLockRef.current = false
    }
  }

  const applyFilter = (ctx: CanvasRenderingContext2D): void => {
    if (selectedFilter?.filterMode === 'lut') {
      applyLUTThenOverlay(ctx, selectedFilter)
    } else if (filterAdjustments) {
      filterUtils.applyAdjustments(ctx, filterAdjustments)
      applyFilterOverlayImage(ctx, selectedFilter?.overlay)
    }
  }

  const startCaptureSession = async (): Promise<void> => {
    if (isExpired) {
      cogoToast.error(t('capture.countdownExpired', 'Time has expired'))
      navigate(`/${projectId}/retake`)
      return
    }

    // Deduct credits only on first capture (not retakes)
    const isFirstCapture = userPreferences.retakeCount === 0 && !isRetakeFromRetakePage
    if (isFirstCapture) {
      // Try to fetch subscription/credits to refresh auth tokens, but don't block session start
      // If offline or fetch fails, proceed anyway - session will queue for upload when online
      const isOnline = navigator.onLine
      if (isOnline) {
        // Online - try to fetch but don't block if it fails
        try {
          await Promise.race([
            fetchSubscriptionAndCredits(),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 3000))
          ])
        } catch (error) {
          // If fetch fails or times out, log but don't block session start
          console.warn('[CAPTURE] Failed to fetch subscription/credits, proceeding anyway:', error)
        }
      } else {
        // Offline - start fetchSubscriptionAndCredits in background but don't wait
        fetchSubscriptionAndCredits().catch((error) => {
          console.warn('[CAPTURE] Background fetchSubscriptionAndCredits failed:', error)
        })
      }

      // Upload session state to trigger backend credit deduction
      // Backend will check if session exists, if user has subscription, etc.
      setTimeout(() => {
        uploadSessionState()
      }, 2000)
    }

    // Start capture recording if enabled
    // CRITICAL: Must start recorder BEFORE starting countdown so frames are recorded during countdown
    if (useRecording && captureCanvasRecorderRef.current) {
      try {
        const recorderStatus = captureCanvasRecorderRef.current.status
        console.log('[Capture] Starting recorder, current status:', recorderStatus)
        
        // Only start if not already recording
        if (recorderStatus !== RecorderStatus.Recording) {
          await captureCanvasRecorderRef.current.start()
          console.log('[Capture] Successfully started capture recorder, new status:', captureCanvasRecorderRef.current.status)
        } else {
          console.log('[Capture] Recorder already recording, skipping start')
        }
      } catch (error) {
        // Don't show error toast - just log and continue without recording
        const errorMsg = error instanceof Error ? error.message : String(error)
        console.error('[Capture] Error starting capture recorder:', errorMsg, error)
        console.warn('[Capture] Continuing without video recording')
        captureCanvasRecorderRef.current = null
        logShotWithoutClip(
          currentPhotoTypeIndex + 1,
          `recorder failed to start: ${errorMsg}`,
          'error'
        )
      }
    } else if (useRecording && !captureCanvasRecorderRef.current) {
      // Try to create recorder if it doesn't exist; use canvas element dimensions (avoids stale canvasDimensions)
      try {
        const canvas = document.querySelector<HTMLCanvasElement>('canvas[data-preview-type="camera"]') ?? document.querySelector('canvas')
        const hasSize = !!canvas && canvas.width > 0 && canvas.height > 0
        const tooSmall = canvas && hasSize ? unrecordableReason(canvas.width, canvas.height) : null
        if (tooSmall) {
          console.warn(`[Capture] Not recording photo ${currentPhotoTypeIndex + 1}: ${tooSmall}`)
          logShotWithoutClip(currentPhotoTypeIndex + 1, tooSmall, 'warn')
        } else if (canvas && hasSize) {
          const canvasContext = canvas.getContext('bitmaprenderer')
          if (canvasContext) {
            const validatedDims = validateDimensions(canvas.width, canvas.height)
            const newRecorder = new Recorder(canvasContext as RenderingContext, {
              name: `dreambooth-${id}-capture-initial`,
              encoderOptions: {
                codec: AVC.getCodec({ profile: 'Baseline', level: '4.1' }),
                width: validatedDims.width,
                height: validatedDims.height,
                bitrate: 2000000
              },
              duration: Infinity,
              download:
                downloadLocal &&
                (userPreferences.recordingType === 'strip_live' ||
                  userPreferences.recordingType === 'strip_story'),
              frameRate: FPS
            })
            captureCanvasRecorderRef.current = newRecorder
            await captureCanvasRecorderRef.current.start()
            console.log('[Capture] Created and started recorder in startCaptureSession')
          }
        }
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : String(error)
        console.warn('[Capture] Failed to create recorder in startCaptureSession:', errorMsg)
        // A recorder whose start() threw never records; don't leave it for handleCapture.
        captureCanvasRecorderRef.current = null
        logShotWithoutClip(
          currentPhotoTypeIndex + 1,
          `recorder failed to start: ${errorMsg}`,
          'error'
        )
      }
    }

    prepCountdownHook.stop()
    setPreparing(false)
    const captureTime = config?.settings.captureCountdown || 3
    captureCountdownHook.setRemainingSeconds(captureTime)
    setCaptureCountdown(captureTime)
    // Autofocus now, while the countdown runs, so the shutter is not waiting on it (camera_prefocus).
    unifiedCameraUtils.prefocus()
    captureCountdownHook.start()
    setCapturing(true)
    // Imperative frame recording: start immediately (no useEffect race)
    runFrameRecordingLoop(1)
  }

  return (
    <div className="w-full h-full bg-newspaper-white font-newspaper text-[#1a1a1a] relative flex flex-col overflow-hidden p-4">
      <div id="flash" className="absolute inset-0 flash z-[6]"></div>

      {/* Shared Countdown Display */}
      <div className="absolute top-8 right-8 z-50">
        <div className="border-2 border-double border-[#2a2a2a] bg-newspaper-white p-6 text-center">
          <div className="text-2xl text-newspaper-black mb-2 font-newspaper">Time Remaining</div>
          <div className="font-bold text-newspaper-black text-5xl font-newspaper">{remainingTime}</div>
        </div>
      </div>

      {/* Frame Preview Area - Takes available space */}
      <div className="flex-1 min-h-0 relative flex items-center justify-center pt-20">
        {/* Full Frame Display */}
        <div
          className="absolute flex items-center justify-center"
          style={{
            width: `${frameWidth * scale}px`,
            height: `${frameHeight * scale}px`
          }}
        >
          {/* Frame Background Image */}
          <FrameCanvasBackdrop frame={selectedFrame} scale={scale} />

          {/* Photo Placeholders with Preview */}
          {photoSlots.map((slot, index) => {
            const currentPhotoType = uniquePhotoTypesArray[currentPhotoTypeIndex]
            const isCurrentSlot = slot.type === currentPhotoType
            const capturedPhotoUrl = capturedPhotosByType.get(slot.type)

            return (
              <div
                key={`${slot.type}-${index}`}
                className="absolute overflow-hidden"
                style={{
                  left: `${slot.left * scale}px`,
                  top: `${slot.top * scale}px`,
                  width: `${slot.width * scale}px`,
                  height: `${slot.height * scale}px`
                }}
              >
                {capturedPhotoUrl ? (
                  <img
                    src={capturedPhotoUrl}
                    alt={`Photo ${slot.type}`}
                    className="w-full h-full object-cover"
                    crossOrigin="anonymous"
                  />
                ) : isCurrentSlot ? (
                  <div className="w-full h-full relative overflow-hidden">
                    <Preview
                      adjustments={filterAdjustments}
                      ratio={getPhotoRatio(slot)}
                      cameraRatio={getNormalizedCaptureRatio()}
                      cameraKey={userPreferences.selectedCamera as 'primary' | 'secondary'}
                      filter={selectedFilter?._id}
                      postLUTAdjustments={selectedFilter?.postLUTAdjustments}
                      onCanvasReady={setCanvasDimensions}
                      fillContainer={true}
                    />
                  </div>
                ) : (
                  <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                    <span className="text-gray-400 text-2xl">
                      {uniquePhotoTypesArray.indexOf(slot.type) + 1}
                    </span>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Prep Countdown Overlay */}
      {preparing && (
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70 flex flex-col z-[3] items-center justify-center">
          <div className="flex flex-col items-center gap-8 max-w-2xl text-center px-8">
            <h1 className="text-8xl text-center text-white font-bold font-newspaper">
              {t('capture.getReady', 'Get Ready!')}
            </h1>
            <p className="text-white/80 text-3xl font-newspaper">
              {t('capture.sessionStarting', 'Session will start in')}{' '}
              <span className="font-bold text-white text-5xl">{prepCountdown}</span>
            </p>
            <button
              type="button"
              onClick={startCaptureSession}
              disabled={!canvasDimensions}
              className="border-2 border-double border-[#2a2a2a] font-newspaper font-medium transition-all duration-200 hover:opacity-90 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap text-4xl bg-[#1a1a1a] text-[#fafaf8] rounded-none shadow-none px-16 py-5 min-w-[320px] tracking-wider"
            >
              {t('capture.startNow', 'Start Now')}
            </button>
            <span className="text-white/60 text-2xl font-newspaper">
              {t('capture.orWaitCountdown', 'or wait for the countdown')}
            </span>
          </div>
        </div>
      )}

      {/* Capture Countdown Overlay */}
      {capturing && (
        <div className="absolute inset-0 z-[4] flex items-center justify-center">
          <span key={captureCountdown} className="text-newspaper-black animate-countdown text-[200px] font-newspaper font-bold">
            {captureCountdown}
          </span>
        </div>
      )}

      {/* Capture Error Message */}
      {isCaptureError && (
        <div className="absolute inset-0 bg-black/80 z-[5] flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-4xl font-bold text-red-500 mb-4">
              {t('capture.captureFailed', 'Capture Failed')}
            </h2>
            <p className="text-xl text-white/80 mb-6">
              {t('capture.stayStillCapturing', 'Please stay still when capturing')}
            </p>
            <button
              type="button"
              onClick={() => {
                setIsCaptureError(false)
                startCaptureSession()
              }}
              className="border-2 border-double border-[#2a2a2a] font-serif font-medium transition-all duration-200 hover:opacity-90 cursor-pointer text-lg bg-[#1a1a1a] text-[#fafaf8] rounded-none shadow-none px-8 py-2.5"
            >
              {t('common.retry', 'Retry')}
            </button>
          </div>
        </div>
      )}

      {/* Button Area - Fixed height, no overlap */}
      <div className="flex-shrink-0 relative h-44">
        {/* Progress Indicator */}
        <div className="absolute bottom-8 right-12 z-10">
          <div className="border-2 border-double border-[#2a2a2a] bg-newspaper-white p-6 text-center">
            <div className="text-2xl text-newspaper-black mb-2 font-newspaper">Progress</div>
            <div className="font-bold text-newspaper-black text-5xl font-newspaper">
              {Math.min(currentPhotoTypeIndex + 1, captureCount)}/{captureCount}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CapturePage

